import { html, type TemplateResult } from 'lit';
import { msg } from '@lit/localize';
import { registerIcons, systemInfoCircle, systemPhone } from '@dertour/ui-components/icons';
import { systemCalendar, systemSell } from '@dertour/ui-components/icons';

registerIcons({ systemInfoCircle, systemPhone, systemCalendar, systemSell });

const formatPhoneHref = (phoneNumber: string): string => `tel:${phoneNumber.replace(/\s+/g, '')}`;

const blocks = {
    tripInfo: ({
        endDateLabel,
        priceLabel,
        startDateLabel,
    }: {
        endDateLabel: string;
        priceLabel: string;
        startDateLabel: string;
    }): TemplateResult => html`
        <div class="m-booking-request__section">
            <div class="m-booking-request__trip-info">
                <div class="m-booking-request__info-list">
                    <der-ui-icon-label
                        icon="systemCalendar"
                        .label=${msg('Reisezeitraum', {
                            desc: 'Section headline for travel dates',
                        })}
                        .value=${`${startDateLabel}-${endDateLabel}`}
                    >
                    </der-ui-icon-label>
                    <der-ui-icon-label
                        icon="systemSell"
                        .label=${msg('Preis pro Person', {
                            desc: 'Section headline for price per person',
                        })}
                        .value=${`${msg('ab', { desc: 'Price prefix (from)' })} ${priceLabel}`}
                    >
                    </der-ui-icon-label>
                </div>
            </div>
        </div>
    `,
    travelDates: ({ lang }: { lang: string }): TemplateResult => html`
        <div class="m-booking-request__section">
            <div class="m-booking-request__headline">
                ${msg('Reisezeitraum', {
                    desc: 'Section headline for travel dates',
                })}
            </div>
            <div class="m-booking-request__textarea-wrapper">
                <div class="m-booking-request__input-wrapper">
                    <der-ui-textarea
                        lang="${lang}"
                        label="${msg('Teilen Sie uns Ihren Wunschzeitraum oder mögliche Alternativen mit', {
                            desc: 'Label for travel dates textarea when no trip info is available',
                        })}"
                        name="travelDates"
                        ?required="${false}"
                        maxlength="3000"
                    ></der-ui-textarea>
                </div>
            </div>
        </div>
    `,

    travellers: ({
        adultPaxCount,
        childrenPaxCount,
        lang,
        onAdultCounterChange,
        onChildrenCounterChange,
    }: {
        adultPaxCount: number;
        childrenPaxCount: number;
        lang: string;
        onAdultCounterChange: (event: CustomEvent<{ count: number }>) => void;
        onChildrenCounterChange: (event: CustomEvent<{ count: number }>) => void;
    }): TemplateResult => html`
        <div class="m-booking-request__section">
            <div class="m-booking-request__headline">
                ${msg('Reiseteilnehmer', {
                    desc: 'Section headline for travellers',
                })}
            </div>
            <div class="m-booking-request__inputs-wrapper">
                <div class="m-booking-request__all-pax-wrapper">
                    <div class="m-booking-request__pax-wrapper">
                        <div class="m-booking-request__pax-header">
                            ${msg('Anzahl Personen', {
                                desc: 'Header for pax count selector',
                            })}
                            <span class="m-booking-request__pax-header--mute"
                                >${msg('(ab 13 Jahre)', {
                                    desc: 'Age specification for adult count selector header',
                                })}</span
                            >
                        </div>
                        <div class="m-booking-request__pax-counter">
                            <der-ui-counter
                                class="m-booking-request__pax-control"
                                .count=${adultPaxCount}
                                .min=${1}
                                .max=${9}
                                variant="reduced"
                                @der-tm-counter-change=${onAdultCounterChange}
                            ></der-ui-counter>
                            <input type="hidden" name="OpportunityPaxAdult" value="${adultPaxCount}" />
                        </div>
                    </div>
                    <div class="m-booking-request__pax-wrapper">
                        <div class="m-booking-request__pax-header">
                            ${msg('Anzahl Kinder', {
                                desc: 'Header for child count selector',
                            })}
                            <span class="m-booking-request__pax-header--mute"
                                >${msg('(bis 12 Jahre)', {
                                    desc: 'Age specification for child count selector header',
                                })}</span
                            >
                        </div>
                        <div class="m-booking-request__pax-counter">
                            <der-ui-counter
                                class="m-booking-request__pax-control"
                                .count=${childrenPaxCount}
                                .min=${0}
                                .max=${8}
                                variant="reduced"
                                @der-tm-counter-change=${onChildrenCounterChange}
                            ></der-ui-counter>
                            <input type="hidden" name="OpportunityPaxChildren" value="${childrenPaxCount}" />
                        </div>
                    </div>
                </div>
            </div>

            ${
                childrenPaxCount > 0
                    ? html`<div class="m-booking-request__pax-age-wrapper">
                          ${Array.from(
                              { length: childrenPaxCount },
                              (_, i) =>
                                  html`<der-ui-dropdown
                                      lang="${lang}"
                                      label="${msg('Alter Kind', { desc: 'Label prefix for child age dropdown' })} ${i + 1}"
                                      name="OpportunityChildrenAge${i + 1}"
                                      .options=${Array.from({ length: 12 }, (_, idx) => ({
                                          id: String(idx + 1),
                                          name: String(idx + 1),
                                      }))}
                                      placeholder="${msg('-', { desc: 'Placeholder for child age dropdown' })}"
                                      ?required=${true}
                                  ></der-ui-dropdown>`
                          )}
                      </div>`
                    : ''
            }
            <div class="m-booking-request__info-wrapper">
                <der-ui-icon-label
                    icon="systemInfoCircle"
                    value="${msg(
                        'Die Richtpreise gelten pro erwachsene Person im Doppelzimmer. Bei ungerader Anzahl Personen kann sich der Gesamtpreis ändern.',
                        {
                            desc: 'Info text below travellers section',
                        }
                    )}"
                ></der-ui-icon-label>
            </div>
        </div>
    `,

    extras: ({ lang }: { lang: string }): TemplateResult => html`
        <div class="m-booking-request__section">
            <div class="m-booking-request__headline">
                ${msg('Optionale Zusatzleistungen', {
                    desc: 'Section headline for extras and change requests',
                })}
            </div>
            <div class="m-booking-request__checkboxes-wrapper">
                <der-ui-checkbox
                    name="extraFlight"
                    class="m-booking-request__checkbox"
                    .label=${msg('Ich wünsche einen Flugofferte dazu', {
                        desc: 'Extra option text: wants flight offer',
                    })}
                ></der-ui-checkbox>
                <der-ui-checkbox
                    name="extraProg"
                    class="m-booking-request__checkbox"
                    .label=${msg('Ich wünsche ein Vor- oder Anschlussprogramm', {
                        desc: 'Extra option text: wants pre/post program',
                    })}
                ></der-ui-checkbox>
                <der-ui-checkbox
                    name="extraTour"
                    class="m-booking-request__checkbox"
                    .label=${msg('Ich wünsche Ausflugsvorschläge', {
                        desc: 'Extra option text: wants tour suggestions',
                    })}
                ></der-ui-checkbox>
            </div>
            <div class="m-booking-request__textarea-wrapper">
                <div class="m-booking-request__input-wrapper">
                    <der-ui-textarea
                        lang="${lang}"
                        label="${msg('Mitteilung', { desc: 'Label for additional information textarea' })}"
                        name="extraData"
                        ?required="${false}"
                        maxlength="3000"
                    ></der-ui-textarea>
                </div>
            </div>
            <div class="m-booking-request__info-wrapper">
                <der-ui-icon-label
                    icon="systemInfoCircle"
                    value="${msg(
                        'Mehrkosten durch Anpassungen oder Zusatzleistungen weisen wir transparent in der Offerte aus.',
                        {
                            desc: 'Info text below extras section',
                        }
                    )}"
                ></der-ui-icon-label>
            </div>
        </div>
    `,

    personalInfo: ({ lang, namePattern }: { lang: string; namePattern: string }): TemplateResult => html`
        <div class="m-booking-request__input-wrapper">
            <der-ui-radio-group
                id="genderGroup"
                name="ContactGender"
                label="${msg('Anrede', { desc: 'Label for salutation / gender radio group' })}"
                lang="${lang}"
                ?required="${true}"
            >
                <der-ui-radio-button
                    value="2"
                    label="${msg('Frau', { desc: 'Female salutation label' })}"
                ></der-ui-radio-button>
                <der-ui-radio-button
                    value="1"
                    label="${msg('Herr', { desc: 'Male salutation label' })}"
                ></der-ui-radio-button>
                <der-ui-radio-button
                    value="3"
                    label="${msg('Divers', { desc: 'Diverse / non-binary salutation label' })}"
                ></der-ui-radio-button>
            </der-ui-radio-group>
        </div>
        <div class="m-booking-request__input-wrapper m-booking-request__multiple-inputs-wrapper">
            <der-ui-input
                lang="${lang}"
                label="${msg('Vorname', { desc: 'Label for first name input' })}"
                name="ContactFirstName"
                ?required="${true}"
                pattern="${namePattern}"
            ></der-ui-input>
            <der-ui-input
                lang="${lang}"
                label="${msg('Nachname', { desc: 'Label for last name input' })}"
                name="ContactLastName"
                ?required="${true}"
                pattern="${namePattern}"
            ></der-ui-input>
        </div>
    `,

    phoneEmail: ({ lang, emailPattern }: { lang: string; emailPattern: string }): TemplateResult => html`
        <div class="m-booking-request__input-wrapper m-booking-request__multiple-inputs-wrapper">
            <der-ui-phone-input
                lang="${lang}"
                label="${msg('Telefon', { desc: 'Label for phone input' })}"
                name="ContactPhone"
                input-placeholder=""
                ?required="${true}"
                validate-message="${msg('Bitte geben Sie eine gültige Telefonnummer ein.', {
                    desc: 'Validation message for invalid phone number',
                })}"
            ></der-ui-phone-input>
            <der-ui-input
                lang="${lang}"
                label="${msg('E-Mail', { desc: 'Label for email input' })}"
                name="ContactEmail"
                inputmode="email"
                pattern="${emailPattern}"
                ?required="${true}"
                type="email"
            ></der-ui-input>
        </div>
    `,

    salesSite: ({
        hasOwningTeam,
        lang,
        onSalesSiteSelected,
    }: {
        hasOwningTeam: boolean;
        lang: string;
        onSalesSiteSelected: (event: CustomEvent) => void;
    }): TemplateResult => {
        if (hasOwningTeam) return html``;

        return html`<div class="m-booking-request__input-wrapper">
            <der-tm-roundtrip-booking-request-sales-sites
                lang="${lang}"
                label="${msg('Wählen Sie Ihr Reisebüro', { desc: 'Label for sales site selection dropdown' })}"
                name="OpportunityOwningTeam"
                placeholder="${msg('Bitte wählen', { desc: 'Placeholder for dropdown select' })}"
                ?required="${true}"
                @sales-site-selected=${onSalesSiteSelected}
            ></der-tm-roundtrip-booking-request-sales-sites>
        </div>`;
    },

    emailConfirmation: (): TemplateResult => html`
        <div class="m-booking-request__email-confirmation">
            <label for="emailConfirmation"
                >${msg('eMail Confirmation', {
                    desc: 'Honeypot field label for spam prevention (kept as-is from legacy implementation)',
                })}</label
            >
            <input
                type="text"
                id="emailConfirmation"
                name="ContactEmailConfirmation"
                tabindex="-1"
                autocomplete="new-password"
            />
        </div>
    `,

    costsConfirmation: (): TemplateResult => html`
        <div class="m-booking-request__checkbox-wrapper">
            <der-ui-checkbox name="OpportunityCostConfirmation" ?required=${true}></der-ui-checkbox>
            <div class="m-booking-request__checkbox-wrapper__text">
                ${msg(
                    'Für den Fall, dass keine Buchung erfolgt, akzeptiere ich eine Beratungspauschale von CHF 150.– (voll anrechenbar bei Buchung).*',
                    { desc: 'Cost confirmation checkbox text' }
                )}
            </div>
        </div>
    `,

    newsletter: (newsletter: string): TemplateResult | '' =>
        newsletter.trim() !== ''
            ? html`<div class="m-booking-request__checkbox-wrapper">
                  <der-ui-checkbox name="ContactNewsletter"></der-ui-checkbox>
                  <div class="m-booking-request__checkbox-wrapper__text">
                      ${msg('Ich möchte den personalisierten Newsletter erhalten.', {
                          desc: 'Newsletter subscription checkbox text',
                      })}
                  </div>
              </div>`
            : '',
    privacyNotice: (): TemplateResult => html`
        <div class="m-booking-request__input-wrapper">
            <div class="m-booking-request__actions-text">
                ${msg('Ihre Daten bearbeiten wir gemäss unserer', { desc: 'Privacy notice text prefix' })}
                <a href="${msg('https://www.kuoni.ch/datenschutz', { desc: 'Privacy policy URL' })}" target="_blank"
                    >${msg('Datenschutzerklärung', { desc: 'Privacy policy link text' })}</a
                >
            </div>
        </div>
    `,

    actions: ({
        isSubmitting,
        missingInternalValueError,
    }: {
        isSubmitting: boolean;
        missingInternalValueError: boolean;
    }): TemplateResult => html`
        <div class="m-booking-request__actions-button">
            ${
                missingInternalValueError
                    ? msg('Es ist leider ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.', {
                          desc: 'Error message for missing internal value',
                      })
                    : ''
            }
            <der-ui-button type="submit" variant="primary" ?disabled=${isSubmitting}
                >${msg('Anfrage absenden', { desc: 'Submit button label for booking request form' })}
            </der-ui-button>
        </div>
    `,

    contactSupport: ({ supportPhone }: { supportPhone: string }): TemplateResult => html`
        <div class="m-booking-request__info-wrapper m-booking-request__contact-support">
            <der-ui-icon-label icon="systemPhone">
                <span slot="value">
                    ${msg('Haben Sie Fragen? Kontaktieren Sie unsere Experten unter', {
                        desc: 'Support contact text prefix below the submit button',
                    })}
                    <a href="${formatPhoneHref(supportPhone)}">${supportPhone}</a>
                </span>
            </der-ui-icon-label>
        </div>
    `,

    contactBase: (data: {
        emailPattern: string;
        hasOwningTeam: boolean;
        isSubmitting: boolean;
        lang: string;
        namePattern: string;
        newsletter: string;
        supportPhone: string;
        missingInternalValueError: boolean;
        onSalesSiteSelected: (event: CustomEvent) => void;
    }): TemplateResult => html`
        ${blocks.personalInfo(data)} ${blocks.phoneEmail(data)} ${blocks.salesSite(data)} ${blocks.emailConfirmation()}
        ${blocks.newsletter(data.newsletter)} ${blocks.privacyNotice()} ${blocks.actions(data)}
        ${data.supportPhone.trim() ? blocks.contactSupport({ supportPhone: data.supportPhone }) : ''}
    `,

    contactWithCosts: (data: {
        emailPattern: string;
        hasOwningTeam: boolean;
        isSubmitting: boolean;
        lang: string;
        namePattern: string;
        newsletter: string;
        supportPhone: string;
        missingInternalValueError: boolean;
        onSalesSiteSelected: (event: CustomEvent) => void;
    }): TemplateResult => html`
        ${blocks.personalInfo(data)} ${blocks.phoneEmail(data)} ${blocks.salesSite(data)} ${blocks.emailConfirmation()}
        ${blocks.newsletter(data.newsletter)} ${blocks.costsConfirmation()} ${blocks.privacyNotice()}
        ${blocks.actions(data)}
        ${data.supportPhone.trim() ? blocks.contactSupport({ supportPhone: data.supportPhone }) : ''}
    `,

    contactSection: (contactContent: TemplateResult): TemplateResult => html`
        <div class="m-booking-request__section">
            <div class="m-booking-request__headline">
                ${msg('Ihre Kontaktdaten', {
                    desc: 'Section headline for contact information',
                })}
            </div>
            <div class="m-booking-request__inputs-wrapper">${contactContent}</div>
        </div>
    `,
};

type BaseVariantData = {
    adultPaxCount: number;
    childrenPaxCount: number;
    emailPattern: string;
    endDateLabel: string;
    formName: string;
    hasOwningTeam: boolean;
    hasTripInfo: boolean;
    isSubmitting: boolean;
    lang: string;
    namePattern: string;
    newsletter: string;
    priceLabel: string;
    startDateLabel: string;
    supportPhone: string;
    tripTitle: string;
    missingInternalValueError: boolean;
    onAdultCounterChange: (event: CustomEvent<{ count: number }>) => void;
    onChildrenCounterChange: (event: CustomEvent<{ count: number }>) => void;
    onSalesSiteSelected: (event: CustomEvent) => void;
    onSubmit: (event: SubmitEvent) => void;
};

export const variantRegistry: Record<string, (data: BaseVariantData) => TemplateResult> = {
    default: (data: BaseVariantData): TemplateResult => html`
        <form name="${data.formName}" novalidate @submit="${data.onSubmit}">
            <div class="m-booking-request__form">
                ${data.hasTripInfo ? blocks.tripInfo(data) : blocks.travelDates(data)} ${blocks.travellers(data)}
                ${blocks.extras(data)} ${blocks.contactSection(blocks.contactBase(data))}
            </div>
        </form>
    `,

    costs: (data: BaseVariantData): TemplateResult => html`
        <form name="${data.formName}" novalidate @submit="${data.onSubmit}">
            <div class="m-booking-request__form">
                ${data.hasTripInfo ? blocks.tripInfo(data) : blocks.travelDates(data)} ${blocks.travellers(data)}
                ${blocks.extras(data)} ${blocks.contactSection(blocks.contactWithCosts(data))}
            </div>
        </form>
    `,
};
